import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent,
  type KeyboardEvent,
} from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

import { HERO_SLIDES, HERO_WIDTHS, SLIDE_INTERVAL_MS } from '../data/hero-slides'

import { Picture } from './picture'

/** True when the visitor has asked the OS to reduce motion. */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
  )

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => setReduced(query.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  return reduced
}

/**
 * Horizontal travel, in px, that separates a deliberate swipe from the few
 * pixels a finger drifts during an ordinary tap.
 */
const SWIPE_THRESHOLD_PX = 50

export function HeroCarousel() {
  const { t } = useTranslation()
  const reducedMotion = usePrefersReducedMotion()
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)

  const total = HERO_SLIDES.length
  const go = useCallback((next: number) => setIndex((next + total) % total), [total])

  // Auto-advance, but not while the visitor is interacting with the controls,
  // not while the tab is in the background, and not at all under reduced motion —
  // an unattended cross-fade is exactly the kind of movement that setting is for.
  //
  // `index` is a dependency so that any manual move — arrow, dot, swipe, key —
  // restarts the countdown. Without it a click could be followed a moment later
  // by an auto-advance that lands on the slide after the one just chosen.
  useEffect(() => {
    if (reducedMotion || paused) return

    const timer = window.setInterval(() => {
      if (!document.hidden) setIndex((current) => (current + 1) % total)
    }, SLIDE_INTERVAL_MS)

    return () => window.clearInterval(timer)
  }, [reducedMotion, paused, total, index])

  // Arrow keys move the carousel whenever focus sits anywhere inside it — the
  // CTAs and the controls are the focusable children, so this covers both.
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (reducedMotion) return
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return

    // Otherwise the browser scrolls the page sideways as well as changing slide.
    event.preventDefault()
    go(event.key === 'ArrowLeft' ? index - 1 : index + 1)
  }

  // Pointer events rather than touch events: one code path covers finger,
  // trackpad drag and pen.
  const swipeStart = useRef<{ x: number; y: number } | null>(null)

  const onPointerDown = (event: PointerEvent<HTMLElement>) => {
    swipeStart.current = reducedMotion ? null : { x: event.clientX, y: event.clientY }
  }

  const onPointerUp = (event: PointerEvent<HTMLElement>) => {
    const start = swipeStart.current
    swipeStart.current = null
    if (!start) return

    const dx = event.clientX - start.x
    const dy = event.clientY - start.y

    // Must be far enough, and more horizontal than vertical — a page scroll that
    // happens to begin on the hero should never change the slide.
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) <= Math.abs(dy)) return

    // Dragging left pulls the next slide in, matching the direction of travel.
    go(dx < 0 ? index + 1 : index - 1)
  }

  const slides = reducedMotion ? HERO_SLIDES.slice(0, 1) : HERO_SLIDES
  const activeIndex = reducedMotion ? 0 : index

  return (
    <section
      id="home"
      // `touch-pan-y` hands vertical gestures back to the page so the hero can
      // still be scrolled past; horizontal ones are ours.
      className="bg-primary relative flex min-h-[600px] touch-pan-y items-center justify-center overflow-hidden lg:h-[calc(100svh-5rem)]"
      aria-roledescription="carousel"
      aria-label={t('hero.carouselLabel')}
      // Focus pauses at the section level: a keyboard visitor reading the CTAs
      // should not have the backdrop change underneath them. Hover does not —
      // see the controls below.
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={() => (swipeStart.current = null)}
    >
      <div className="absolute inset-0" aria-hidden={false}>
        {slides.map((slide, slideIndex) => (
          <div
            key={slide.image}
            className={cn(
              'absolute inset-0 transition-opacity duration-1000 ease-in-out',
              slideIndex === activeIndex ? 'opacity-100' : 'opacity-0',
            )}
            // Slides that are not showing are hidden from the accessibility tree,
            // so a screen reader announces one image, not three.
            aria-hidden={slideIndex !== activeIndex}
          >
            <Picture
              name={slide.image}
              alt={t(slide.altKey)}
              widths={HERO_WIDTHS}
              sizes="100vw"
              priority={slideIndex === 0}
              className="size-full object-cover"
            />
          </div>
        ))}
        <div className="hero-scrim absolute inset-0" />
      </div>

      <div className="max-w-content px-margin-mobile py-stack-lg md:px-margin-tablet lg:px-margin-desktop relative z-10 mx-auto flex w-full flex-col items-center text-center">
        {/* Single line from `md` up — the type scale is tuned so the longest
            title fits. Narrower than that it wraps, balanced across lines. */}
        <h1 className="font-display text-display-lg text-on-primary text-balance drop-shadow-lg md:whitespace-nowrap">
          {t('hero.title')}
        </h1>
        <p className="mt-stack-md text-body-lg text-on-primary/90 max-w-2xl drop-shadow-md">
          {t('hero.subtitle')}
        </p>
        <div className="mt-stack-lg gap-stack-md flex flex-col sm:flex-row">
          <a
            href="#services"
            className="bg-secondary text-label-md text-on-secondary shadow-e2 hover:bg-secondary-container rounded-md px-8 py-3 font-medium transition-colors duration-200"
          >
            {t('hero.ctaPrimary')}
          </a>
          <a
            href="#contact"
            className="border-on-primary text-label-md text-on-primary hover:bg-on-primary/10 rounded-md border-2 px-8 py-3 font-medium transition-colors duration-200"
          >
            {t('hero.ctaSecondary')}
          </a>
        </div>
      </div>

      {!reducedMotion && (
        <div
          // Hover pauses here and not on the <section>: the hero is a full
          // viewport tall, so pausing on it meant a cursor resting anywhere on
          // the page froze the slideshow for good. Aiming at the controls is a
          // deliberate act; resting the mouse is not.
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          className="gap-stack-md absolute inset-x-0 bottom-8 z-10 mx-auto flex w-fit items-center justify-center"
        >
          <button
            type="button"
            aria-label={t('hero.previousSlide')}
            onClick={() => go(index - 1)}
            className="text-on-primary/70 hover:bg-on-primary/10 hover:text-on-primary rounded-md p-2 transition-colors"
          >
            <ChevronLeft className="size-5" />
          </button>

          <div className="flex items-center gap-2">
            {HERO_SLIDES.map((slide, slideIndex) => (
              <button
                key={slide.image}
                type="button"
                aria-label={t('hero.goToSlide', { number: slideIndex + 1, total })}
                aria-current={slideIndex === index ? 'true' : undefined}
                onClick={() => setIndex(slideIndex)}
                className={cn(
                  'h-1 rounded-sm transition-all duration-300',
                  slideIndex === index
                    ? 'bg-secondary w-10'
                    : 'bg-on-primary/40 hover:bg-on-primary/70 w-6',
                )}
              />
            ))}
          </div>

          <button
            type="button"
            aria-label={t('hero.nextSlide')}
            onClick={() => go(index + 1)}
            className="text-on-primary/70 hover:bg-on-primary/10 hover:text-on-primary rounded-md p-2 transition-colors"
          >
            <ChevronRight className="size-5" />
          </button>
        </div>
      )}
    </section>
  )
}
